<script setup lang="ts">
import {
  DIRECTION_DELIVERY,
  DIRECTION_PICKUP,
  loadingUnitRegex,
} from "@/utils/constants";
import { HtmlHTMLAttributes, reactive, ref } from "vue";
import AutoComplete from "../AutoComplete.vue";
import store from "@/store";
import { Terminal } from "@/data/interfaces/TerminalInterface";
import { useRouter } from "vue-router";
import { i18n } from "@/i18n/i18n";

interface FormValidation {
  loading_unit_formatted: string;
}

const router = useRouter();
const { t } = i18n.global;

let formValidations = reactive({
  loading_unit_formatted: { invalid: "false", rx: loadingUnitRegex },
});

const terminals = ref(
  store.state.OrderStore.terminals.map((terminal) => terminal.terminalName)
);
const order = ref({
  directionType: false as string | boolean,
  loading_unit_formatted: "",
  terminal: null,
  primaryReference: "",
  terminalName: "",
});
const showError = ref("");

const selectTerminal = (name: string) => {
  showError.value = "";
  order.value.terminal =
    store.state.OrderStore.terminals.find(
      (terminal: Terminal) => terminal.terminalName == name
    ) ?? null;
  order.value.terminalName = order.value?.terminal?.terminalName ?? "";
};

const validateInput = (
  input: string,
  rx: RegExp,
  formValidationsKey: keyof FormValidation
) => {
  if (input) {
    formValidations[formValidationsKey].invalid = `${!rx.test(input)}`;
  } else {
    formValidations[formValidationsKey].invalid = "true";
  }
  formValidations = { ...formValidations };
};

const showErrors = (formValidationsKey: keyof FormValidation) => {
  let errorType = "Save";
  return formValidations[formValidationsKey].invalid === "true"
    ? t(`order.error${errorType}`)
    : undefined;
};

const fieldEmpty = () => {
  let disabled = true;
  if (order.value.terminal) {
    // if user has deleted part or all of this field, make them type and select again
    if (order.value.terminalName != order.value.terminal.terminalName) {
      order.value.terminal = null;
    }
  }
  disabled =
    !order.value.directionType ||
    !order.value.terminal ||
    !order.value.primaryReference;

  if (order.value.directionType == DIRECTION_PICKUP) {
    disabled =
      !order.value.loading_unit_formatted ||
      formValidations.loading_unit_formatted.invalid === "true" ||
      !order.value.directionType ||
      !order.value.terminal ||
      !order.value.primaryReference;
  }

  return disabled;
};

const save = async () => {
  await store.dispatch("toggleLoading", true);
  if (fieldEmpty()) {
    showError.value = t("order.error_fields_missing");
    await store.dispatch("toggleLoading", false);
  } else {
    showError.value = "";
    try {
      await store.dispatch("OrderStore/saveOrder", order.value);
      await store.dispatch("toggleLoading", false);
      router.push("/");
    } catch (e) {
      if (e.message === "Order not found") {
        showError.value = t("order.error_not_found");
      } else {
        showError.value = t("order.error_technical");
      }
      await store.dispatch("toggleLoading", false);
    }
  }
};

const selectDirection = (event: HtmlHTMLAttributes) => {
  showError.value = "";
  order.value.directionType = event?.target?.value;
};
</script>
<template>
  <div
    id="order-create"
    class="min-w-full w-full z-20 overflow-auto bg-white rounded-lg shadow-xl"
  >
    <div class="flex flex-col gap-6 p-2 pt-5 mx-2 border-b">
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[120px]"
          >{{ $t("order.directionType") }}:</span
        >
        <db-select
          @change="selectDirection"
          v-model="order.directionType"
          :value="order.directionType"
          :label="!order.directionType ? $t('order.directionType') : ''"
          class="w-full hydrated text-gray-300"
        >
          <option selected></option>
          <option
            v-for="direction in [DIRECTION_PICKUP, DIRECTION_DELIVERY]"
            :key="direction"
            :value="direction"
            :selected="order.directionType == direction"
          >
            {{ $t(direction) }}
          </option>
        </db-select>
      </div>

      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[120px]"
          >{{ $t("order.terminal") }}:</span
        >
        <AutoComplete
          v-model="order.terminalName"
          :options="terminals"
          :placeholder="$t('order.terminal')"
          @onSelect="selectTerminal"
          icon="close"
        ></AutoComplete>
      </div>

      <div
        class="w-full flex items-center gap-2"
        :class="order.directionType !== DIRECTION_PICKUP ? 'mb-10' : ''"
      >
        <span class="text-lg text-left w-[120px]"
          >{{ $t("order.reference") }}:</span
        >
        <AutoComplete
          v-model="order.primaryReference"
          :placeholder="$t('order.reference')"
          icon="close"
        ></AutoComplete>
      </div>
      <div
        v-if="order.directionType === DIRECTION_PICKUP"
        class="w-full flex items-center mb-10 gap-2"
      >
        <span class="text-lg text-left w-[120px]"
          >{{ $t("order.loading_unit") }}:</span
        >
        <AutoComplete
          v-model="order.loading_unit_formatted"
          :placeholder="$t('order.loading_unit')"
          :ariainvalid="formValidations['loading_unit_formatted'].invalid"
          :error-message="showErrors('loading_unit_formatted')"
          v-on:update:modelValue="
            () =>
              validateInput(
                order.loading_unit_formatted,
                loadingUnitRegex,
                'loading_unit_formatted'
              )
          "
          icon="close"
        ></AutoComplete>
      </div>
      <span v-if="showError" class="flex justify-center showError"
        ><db-icon icon="error" variant="24-outline" lass="hydrated"></db-icon>
        <p>
          {{ showError }}
        </p></span
      >
    </div>
    <div class="flex flex-col gap-6 p-2 mx-2">
      <div class="w-full flex justify-between items-center gap-2">
        <db-button
          @click="$router.push('/')"
          class="w-full hydrated"
          variant="secondary-solid"
          >{{ $t("order.cancel") }}</db-button
        >
        <db-button
          @click="save"
          :class="fieldEmpty() ? 'disabled' : ''"
          class="w-full hydrated"
          variant="brand-primary"
          >{{ $t("order.add") }}</db-button
        >
      </div>
    </div>
  </div>
</template>
<style>
.showError {
  color: #ec0016;
}
.disabled button {
  color: #ffffff80;
  background-color: #ec001640 !important;
  cursor: default;
  pointer-events: none;
}
</style>
